import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NavbarProps } from '../../types';
import { useScrollNav } from '../../contexts/scrollNavContext';
import { Modal } from '../ui';
import { navigateToLaunchSection } from '../../utils/launchNav';

function isNavItemActive(href: string, pathname: string, hash: string): boolean {
  if (href === '/acheter') {
    return (
      (pathname === '/acheter' || pathname.startsWith('/acheter/')) &&
      !pathname.includes('/terrains')
    );
  }
  if (href === '/acheter/terrains') {
    return pathname.includes('/terrains');
  }
  if (href === '/nos-realisations') {
    return pathname === '/nos-realisations';
  }
  if (href === '/blog') {
    return pathname === '/blog' || pathname.startsWith('/blog/');
  }
  if (href === '/nous-decouvrir') {
    return pathname === '/nous-decouvrir' || pathname.startsWith('/nous-decouvrir/');
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const Navbar: React.FC<NavbarProps> = ({
  items,
  logo,
  cta,
  className = '',
  navigationLocked = false,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const { navVisible, isAutoHideNavPage } = useScrollNav();
  const hideNavOnScroll = isAutoHideNavPage && !navVisible;

  const navLinkClass = (href: string, mobile = false) => {
    const active = isNavItemActive(href, pathname, hash);
    if (mobile) {
      return active
        ? 'font-semibold text-neutral-900 bg-neutral-50'
        : 'font-medium text-neutral-700 hover:text-neutral-900';
    }
    return active
      ? 'font-semibold text-neutral-900'
      : 'font-medium text-neutral-700 hover:text-neutral-900';
  };

  const navItemClass =
    'rounded-lg px-3 py-2 text-[14px] font-medium leading-none tracking-normal transition-colors duration-normal whitespace-nowrap flex items-center gap-1.5 lg:text-[15px]';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setOpenMobileSubmenu(null); // Fermer les sous-menus mobiles quand on ferme le menu
    }
  };

  const handleItemClick = () => {
    setIsMobileMenuOpen(false);
    setOpenSubmenu(null); // Fermer tous les sous-menus ouverts
    setOpenMobileSubmenu(null); // Fermer tous les sous-menus mobiles ouverts
  };

  const handleSubmenuToggle = (itemLabel: string) => {
    setOpenSubmenu(openSubmenu === itemLabel ? null : itemLabel);
  };

  const handleMobileSubmenuToggle = (itemLabel: string) => {
    setOpenMobileSubmenu(openMobileSubmenu === itemLabel ? null : itemLabel);
  };

  // Fermer les sous-menus quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (navigationLocked) {
      navigateToLaunchSection(navigate, pathname, '/vendre');
      handleItemClick();
      return;
    }
    setIsContactModalOpen(true);
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsUserModalOpen(true);
  };

  const handleLaunchNav = (href: string) => {
    navigateToLaunchSection(navigate, pathname, href);
    handleItemClick();
    setOpenSubmenu(null);
  };

  const sortDropdownChildren = (
    parentLabel: string,
    children: { label: string; href: string }[]
  ) => {
    if (parentLabel === 'Acheter') {
      const order = [
        'Terrains à bâtir',
        'Maisons',
        'Appartements',
        'Bureaux',
        "Locaux d'activités",
      ];
      return [...children].sort(
        (a, b) => order.indexOf(a.label) - order.indexOf(b.label)
      );
    }
    return children;
  };

  const getDescriptionForLabel = (label: string): string => {
    const descriptions: Record<string, string> = {
      'Terrains à bâtir': 'Lots constructibles en Bretagne Sud',
      Appartements: 'Programmes neufs et résidences',
      Maisons: 'Maisons individuelles neuves',
      Bureaux: 'Espaces professionnels à acquérir',
      "Locaux d'activité": 'Locaux commerciaux et artisanaux',
      "Locaux d'activités": 'Locaux commerciaux et artisanaux',
      'Vendre à Proméa': 'Confiez-nous votre projet de vente',
      'Nos offres de rachat': 'Solutions de reprise foncière',
      'Estimer mon bien': 'Estimation rapide en ligne',
      'Prendre rendez-vous': 'Échangez avec un conseiller',
      'À propos': 'Notre histoire et nos valeurs',
      'Espaces de ventes': 'Rencontrez nos équipes sur place',
      'Engagements RSE': 'Nos actions responsables',
    };
    return descriptions[label] ?? 'En savoir plus';
  };

  const renderSimpleDropdown = (
    parentLabel: string,
    children: { label: string; href: string }[]
  ) => {
    const sortedChildren = sortDropdownChildren(parentLabel, children);
    const useTwoColumns = sortedChildren.length > 3;

    return (
      <div
        className="absolute left-1/2 top-full z-[101] mt-3 w-[min(calc(100vw-2.5rem),32rem)] -translate-x-1/2"
        role="menu"
        aria-label={parentLabel}
      >
        <div className="overflow-hidden rounded-xl border border-neutral-200/90 bg-white/95 shadow-soft-lg backdrop-blur-xl ring-1 ring-black/[0.03]">
          <div
            className={`p-3 ${
              useTwoColumns ? 'grid grid-cols-1 gap-1 sm:grid-cols-2' : 'flex flex-col gap-1'
            }`}
          >
            {sortedChildren.map((child) => (
              navigationLocked ? (
                <button
                  key={child.label}
                  type="button"
                  role="menuitem"
                  className="group block w-full rounded-lg border border-transparent px-4 py-3.5 text-left transition-colors duration-200 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/20"
                  onClick={() => handleLaunchNav(child.href)}
                >
                  <p className="text-[0.9375rem] font-medium leading-snug text-neutral-900">
                    {child.label}
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
                    {getDescriptionForLabel(child.label)}
                  </p>
                </button>
              ) : (
              <Link
                key={child.label}
                to={child.href}
                role="menuitem"
                className="group block rounded-lg border border-transparent px-4 py-3.5 transition-colors duration-200 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/20"
                onClick={() => {
                  handleItemClick();
                  setOpenSubmenu(null);
                }}
              >
                <p className="text-[0.9375rem] font-medium leading-snug text-neutral-900">
                  {child.label}
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
                  {getDescriptionForLabel(child.label)}
                </p>
              </Link>
              )
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Overlay avec effet de flou pour les sous-menus */}
      {openSubmenu && (
        <div
          className="fixed inset-0 z-[50] bg-black/20 backdrop-blur-[2px] transition-opacity duration-200"
          onClick={() => setOpenSubmenu(null)}
          aria-hidden="true"
        />
      )}

      <div
        data-scroll-lock-padding
        className={`fixed inset-x-0 top-0 z-[100] flex justify-center px-3 pt-2 transition-transform duration-300 ease-apple sm:px-4 sm:pt-3 md:px-6 md:pt-4 ${
          hideNavOnScroll ? '-translate-y-[calc(100%+1.25rem)]' : 'translate-y-0'
        } ${className}`}
      >
        <nav
          ref={navRef}
          className="glass-liquid relative w-full max-w-[1440px] overflow-x-clip overflow-y-visible rounded-xl"
        >
        <div className="relative z-[1] px-3 py-1.5 sm:px-4 lg:px-5">
        <div className="relative flex min-h-12 items-center justify-between sm:min-h-14">
          {/* Logo */}
          <div className="flex-shrink-0">
            {navigationLocked ? (
              <button
                type="button"
                className="block"
                onClick={() => navigateToLaunchSection(navigate, pathname, '/')}
                aria-label="Provéa, accueil"
              >
                {logo}
              </button>
            ) : (
              <Link to="/" className="block">
                {logo}
              </Link>
            )}
          </div>

          {/* Navigation Desktop - Centrée */}
          <div className="hidden lg:block absolute left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-0.5 xl:gap-1.5">
              {items.map((item) => (
                <div key={item.label} className="relative">
                  {item.children && item.children.length > 0 ? (
                    // Élément avec sous-menu - utilise un button
                    <button
                      className={`${navItemClass} ${
                        openSubmenu === item.label
                          ? 'font-semibold text-neutral-900'
                          : navLinkClass(item.href)
                      }`}
                      onClick={() => handleSubmenuToggle(item.label)}
                    >
                      {item.label}
                      <svg
                        className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                          openSubmenu === item.label ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  ) : navigationLocked ? (
                    <button
                      type="button"
                      className={`${navItemClass} ${navLinkClass(item.href)}`}
                      onClick={() => handleLaunchNav(item.href)}
                    >
                      {item.label}
                    </button>
                  ) : (
                    // Élément sans sous-menu - utilise un Link pour la navigation directe
                    <Link
                      to={item.href}
                      className={`${navItemClass} ${navLinkClass(item.href)}`}
                      onClick={() => handleItemClick()}
                    >
                      {item.label}
                    </Link>
                  )}
                  
                  {/* Sous-menu simplifié */}
                  {item.children &&
                    item.children.length > 0 &&
                    openSubmenu === item.label &&
                    renderSimpleDropdown(item.label, item.children)}
                </div>
              ))}
            </div>
          </div>

          {/* CTA et Icônes de contact Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {cta && (
              <div className="hidden xl:block">
                {cta}
              </div>
            )}
            <button
              onClick={handlePhoneClick}
              className="rounded-md px-3.5 py-2 text-[14px] font-medium text-orange-500 transition-colors hover:text-orange-600 lg:text-[15px]"
              aria-label="Vendre un terrain"
            >
              Vendre un terrain
            </button>
            <button
              onClick={handleUserClick}
              className="rounded-pill bg-primary-500 px-5 py-2 text-[14px] font-medium text-white transition-colors hover:bg-primary-600 lg:text-[15px]"
              aria-label="Connexion / Inscription"
            >
              Connexion
            </button>
          </div>

          {/* Bouton menu mobile */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center rounded-md p-2 text-neutral-700 transition-colors duration-normal hover:bg-neutral-50 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-expanded={isMobileMenuOpen}
              aria-label="Menu principal"
            >
              <span className="sr-only">Ouvrir le menu principal</span>
              {!isMobileMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      {isMobileMenuOpen && (
        <div className="lg:hidden">
          <div className="space-y-1 border-t border-neutral-200 bg-white/98 px-2 py-3 sm:px-3">
            {items.map((item) => (
              <div key={item.label}>
                {item.children && item.children.length > 0 ? (
                  // Élément avec sous-menu - utilise un button
                  <button
                    className={`block w-full text-left px-3 py-2 text-base font-medium transition-colors duration-normal flex items-center justify-between ${
                      openMobileSubmenu === item.label
                        ? 'text-primary-500 bg-primary-50'
                        : navLinkClass(item.href, true)
                    }`}
                    onClick={() => handleMobileSubmenuToggle(item.label)}
                  >
                    {item.label}
                    <svg 
                      className={`w-4 h-4 transition-transform duration-200 ${
                        openMobileSubmenu === item.label ? 'rotate-90' : ''
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : navigationLocked ? (
                  <button
                    type="button"
                    className={`block w-full px-3 py-2 text-left text-base font-medium transition-colors duration-normal flex items-center justify-between ${navLinkClass(item.href, true)}`}
                    onClick={() => handleLaunchNav(item.href)}
                  >
                    {item.label}
                  </button>
                ) : (
                  // Élément sans sous-menu - utilise un Link pour la navigation directe
                  <Link
                    to={item.href}
                    className={`block px-3 py-2 text-base font-medium transition-colors duration-normal flex items-center justify-between ${navLinkClass(item.href, true)}`}
                    onClick={() => handleItemClick()}
                  >
                    {item.label}
                  </Link>
                )}
                
                {/* Sous-menu mobile */}
                {item.children && item.children.length > 0 && openMobileSubmenu === item.label && (
                  <div className="mx-2 my-2 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
                    <div className="space-y-0.5 p-2">
                      {sortDropdownChildren(item.label, item.children).map((child) => (
                        navigationLocked ? (
                          <button
                            key={child.label}
                            type="button"
                            className="group block w-full rounded-xl px-3 py-3 text-left transition-colors active:bg-neutral-50"
                            onClick={() => handleLaunchNav(child.href)}
                          >
                            <span className="block text-sm font-semibold text-neutral-900 group-hover:text-primary-800">
                              {child.label}
                            </span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-neutral-500">
                              {getDescriptionForLabel(child.label)}
                            </span>
                          </button>
                        ) : (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="group block rounded-xl px-3 py-3 transition-colors active:bg-neutral-50"
                          onClick={() => handleItemClick()}
                        >
                          <span className="block text-sm font-semibold text-neutral-900 group-hover:text-primary-800">
                            {child.label}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-neutral-500">
                            {getDescriptionForLabel(child.label)}
                          </span>
                        </Link>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {/* CTA et Icônes de contact mobile */}
            <div className="pt-4 border-t border-neutral-200 space-y-4">
              {/* CTA Button mobile */}
              {cta && (
                <div className="flex justify-center">
                  {cta}
                </div>
              )}
              
              {/* Boutons de contact */}
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={handlePhoneClick}
                  className="flex-1 rounded-xl border border-orange-500 bg-transparent px-4 py-2.5 text-sm font-semibold text-orange-500 transition-all hover:bg-orange-500/10"
                  aria-label="Vendre un terrain"
                >
                  Vendre un terrain
                </button>
                <button
                  onClick={handleUserClick}
                  className="flex-1 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-600"
                  aria-label="Connexion / Inscription"
                >
                  Connexion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        </nav>
      </div>

      {/* Modal de contact */}
      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Contactez Proméa"
        size="lg"
      >
        <div className="space-y-6">
          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coordonnées */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Nos coordonnées</h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Adresse</p>
                    <p className="text-neutral-600">123 Avenue de la République<br />56000 Vannes, France</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 text-primary-500">
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Téléphone</p>
                    <a href="tel:+33297012345" className="text-primary-500 hover:text-primary-600 transition-colors">
                      02 97 01 23 45
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 text-primary-500">
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Email</p>
                    <a href="mailto:contact@provea-immo.fr" className="text-primary-500 hover:text-primary-600 transition-colors">
                      contact@provea-immo.fr
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Horaires */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Horaires d'ouverture</h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Lundi - Vendredi</span>
                  <span className="font-medium text-neutral-900">9h00 - 18h00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Samedi</span>
                  <span className="font-medium text-neutral-900">9h00 - 12h00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Dimanche</span>
                  <span className="font-medium text-neutral-900">Fermé</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                <p className="text-sm text-primary-700">
                  <strong>Rendez-vous sur demande</strong><br />
                  Nous nous adaptons à vos disponibilités pour vous rencontrer.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200">
            <a
              href="tel:+33297012345"
              className="flex-1 bg-primary-500 text-white px-6 py-3 rounded-md font-medium hover:bg-primary-600 transition-colors text-center"
            >
              Appeler maintenant
            </a>
            <a
              href="mailto:contact@provea-immo.fr"
              className="flex-1 bg-white text-primary-500 px-6 py-3 rounded-md font-medium border border-primary-500 hover:bg-primary-50 transition-colors text-center"
            >
              Envoyer un email
            </a>
          </div>
        </div>
      </Modal>

      {/* Modal utilisateur */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        title="Espace client"
        size="md"
      >
        <div className="space-y-6">
          {/* Message d'accueil */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              {navigationLocked ? 'Espace client' : 'Bienvenue sur Provéa'}
            </h3>
            <p className="text-neutral-600">
              {navigationLocked
                ? 'Pour toute demande, contactez directement nos équipes. Nous vous répondons rapidement.'
                : 'Connectez-vous à votre espace client ou créez un compte pour accéder à vos projets.'}
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="space-y-3">
            {!navigationLocked ? (
              <>
            <button
              onClick={() => {
                setIsUserModalOpen(false);
                // Ici vous pouvez ajouter la logique de connexion
                // TODO: Implémenter la logique de connexion
              }}
              className="w-full bg-primary-500 text-white px-6 py-3 rounded-md font-medium hover:bg-primary-600 transition-colors text-center"
            >
              Se connecter
            </button>
            
            <button
              onClick={() => {
                setIsUserModalOpen(false);
                // Ici vous pouvez ajouter la logique d'inscription
                // TODO: Implémenter la logique d'inscription
              }}
              className="w-full bg-white text-primary-500 px-6 py-3 rounded-md font-medium border border-primary-500 hover:bg-primary-50 transition-colors text-center"
            >
              Créer un compte
            </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsUserModalOpen(false);
                  navigateToLaunchSection(navigate, pathname, '/vendre');
                }}
                className="w-full bg-orange-500 text-white px-6 py-3 rounded-md font-medium hover:bg-orange-600 transition-colors text-center"
              >
                Nous contacter
              </button>
            )}
          </div>

          {/* Informations supplémentaires */}
          <div className="pt-4 border-t border-neutral-200">
            <p className="text-sm text-neutral-500 text-center">
              Vous avez des questions ?{' '}
              <button
                onClick={() => {
                  setIsUserModalOpen(false);
                  setIsContactModalOpen(true);
                }}
                className="text-primary-500 hover:text-primary-600 font-medium"
              >
                Contactez-nous
              </button>
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Navbar;
